import { createHash } from "node:crypto";
import * as fs from "node:fs";
import * as os from "node:os";
import * as path from "node:path";
import { resolveRepoRoot } from "./brain-paths.js";

// Architect drift detection via a render-time sidecar manifest + source content hash.
//
// ── canonical hash (MUST stay byte-for-byte identical to the writer in
// scripts/write-skill-sidecar.mjs — both hash the entire source file):
//
//   createHash("sha256").update(readFileSync(sourcePath)).digest("hex")

/**
 * Read the `version:` frontmatter field from a markdown file.
 * Returns "" when the file is missing or has no version field.
 */
function readVersionFrontmatter(file: string): string {
  if (!fs.existsSync(file)) return "";
  const text = fs.readFileSync(file, "utf8");
  // Frontmatter is delimited by a leading `---` line and a closing `---` line.
  if (!text.startsWith("---")) return "";
  const closeIdx = text.indexOf("\n---", 3);
  if (closeIdx < 0) return "";
  const fm = text.slice(3, closeIdx + 1);
  const m = /^version:\s*["']?([^"'\n]+)["']?\s*$/m.exec(fm);
  return m ? m[1].trim() : "";
}

/**
 * Compute SHA-256 hex digest of a file's raw bytes.
 * Canonical algorithm — must stay in sync with scripts/write-skill-sidecar.mjs.
 */
function sha256File(filePath: string): string {
  return createHash("sha256").update(fs.readFileSync(filePath)).digest("hex");
}

export interface ArchitectDrift {
  match: boolean;
  sourceVersion: string;       // from source frontmatter (display)
  installedVersion: string;    // from sidecar (display); "" when no/unreadable sidecar
  advice?: string;             // human-readable next step
}

export function checkArchitectDrift(): ArchitectDrift {
  // 1. Resolve paths
  const sourcePath = path.join(
    resolveRepoRoot(),
    "personas",
    "m8t-architect",
    "persona.md",
  );
  const sidecarPath = path.join(
    os.homedir(),
    ".claude",
    "skills",
    "m8t-architect",
    ".m8t-skill.json",
  );

  // 2. Source missing
  if (!fs.existsSync(sourcePath)) {
    return {
      match: false,
      sourceVersion: "",
      installedVersion: "",
      advice: `Source persona not found at ${sourcePath}. Check ~/.m8t-stack/repo-root points at a valid checkout.`,
    };
  }

  // 3. Read source version (for display)
  const sourceVersion = readVersionFrontmatter(sourcePath);

  // 4. Sidecar missing or unreadable/malformed
  let sidecar: { version?: string; sourceSha256?: string } | null = null;
  if (fs.existsSync(sidecarPath)) {
    try {
      const raw: unknown = JSON.parse(fs.readFileSync(sidecarPath, "utf8"));
      if (raw !== null && typeof raw === "object") {
        const r = raw as Record<string, unknown>;
        sidecar = {
          version: typeof r.version === "string" ? r.version : undefined,
          sourceSha256: typeof r.sourceSha256 === "string" ? r.sourceSha256 : undefined,
        };
      }
    } catch {
      sidecar = null; // malformed JSON → treat as missing
    }
  }

  if (!sidecar || typeof sidecar.sourceSha256 !== "string") {
    return {
      match: false,
      sourceVersion,
      installedVersion: "",
      advice: "Architect skill not installed (or installed before sidecar support). Run `bash guides/install/m8t.md` to render it.",
    };
  }

  // 5. Compare hashes
  const installedVersion = typeof sidecar.version === "string" ? sidecar.version : "";
  const currentHash = sha256File(sourcePath);

  if (currentHash === sidecar.sourceSha256) {
    return { match: true, sourceVersion, installedVersion };
  }

  const sameVersion = !!installedVersion && installedVersion === sourceVersion;
  return {
    match: false,
    sourceVersion,
    installedVersion,
    advice: sameVersion
      ? `m8t-architect content drift (v${sourceVersion} unchanged, body edited since render). Re-render via \`bash guides/install/m8t.md\`.`
      : `Installed m8t-architect${installedVersion ? ` (v${installedVersion})` : ""} is out of date with source${sourceVersion ? ` (v${sourceVersion})` : ""}. Run \`bash guides/install/m8t.md\` to re-render.`,
  };
}
